import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/ui/Icon';
import { ICONS } from '@/constants/icons';
import { PlatformPreviewProps } from '../types';
import { PLATFORM_CONFIG, truncateForPlatform } from '../platformConfig';
import { PreviewAvatar } from '../components/PreviewAvatar';
import { PreviewCaption } from '../components/PreviewCaption';
import { MediaDisplay } from '../components/MediaDisplay';
import { EmptyState } from '../components/EmptyState';

export const ThreadsPreview = ({ caption, media, isCarousel, channel, activeIndex, onIndexChange }: PlatformPreviewProps) => {
  const { t } = useTranslation('common');
  const config = PLATFORM_CONFIG.threads;
  const displayCaption = truncateForPlatform(caption, 'threads');
  const isEmpty = !caption && media.length === 0;

  const handle = channel?.handle ?? '';

  if (isEmpty) return <EmptyState />;

  return (
    <div className="bg-white w-full min-w-0 max-w-[600px] mx-auto border-b border-gray-200">
      <div className="p-4 flex gap-3 relative">
        {/* Avatar with thread line */}
        <div className="relative flex flex-col items-center flex-shrink-0">
          <PreviewAvatar colorClass={config.avatarColor} size="md" avatarUrl={channel?.avatarUrl} initials={channel?.initials} />
          {/* Vertical thread line */}
          <div className="absolute top-12 bottom-0 w-0.5 bg-gray-200" />
        </div>

        <div className="flex-1 min-w-0">
          {/* Header */}
          <div className="flex min-w-0 items-center gap-2">
            <span className="min-w-0 truncate font-bold text-gray-900 text-[15px]">{handle}</span>
            <span className="flex-shrink-0 text-gray-400 text-[15px]">{t('preview_now')}</span>
          </div>

          {/* Content */}
          {caption && (
            <PreviewCaption
              caption={displayCaption}
              hashtagColor={config.hashtagColor}
              className="text-gray-900 text-[15px] mt-1"
              truncated={caption.length > 150}
            />
          )}

          {/* Media */}
          {media.length > 0 && (
            <div className="mt-3">
              <MediaDisplay
                media={isCarousel ? media : media.slice(0, 1)}
                aspectRatio="auto"
                rounded="lg"
                showCarouselDots={isCarousel && media.length > 1}
                activeIndex={activeIndex}
                onIndexChange={onIndexChange}
              />
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center gap-4 mt-3 text-gray-500">
            <button type="button" className="hover:text-gray-900 transition-colors">
              <Icon name={ICONS.HEART} size="sm" />
            </button>
            <button type="button" className="hover:text-gray-900 transition-colors">
              <Icon name={ICONS.CHAT} size="sm" />
            </button>
            <button type="button" className="hover:text-gray-900 transition-colors">
              <Icon name={ICONS.REFRESH} size="sm" />
            </button>
            <button type="button" className="hover:text-gray-900 transition-colors">
              <Icon name={ICONS.PAPER_PLANE} size="sm" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
